export const namespaced = true;

import TeacherService from '@/services/TeacherService.js';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { basicPresent, basicSubFieldPresent } from '@/presenters/fields';

export const state = {
  teachers: [],
  teacher: {},
  editingTeacher: null,
  tamaiClasses: [],
  allTamaiClasses: [],
};

export const mutations = {
  SET_TEACHERS(state, teachers) {
    state.teachers = teachers;
  },
  CLEAR_TEACHERS(state) {
    state.teachers = [];
  },
  SET_TEACHER(state, teacher) {
    state.teacher = teacher;
  },
  SET_EDITING_TEACHER(state, teacher) {
    state.editingTeacher = teacher;
  },
  DELETE_EDITING_TEACHER(state) {
    state.editingTeacher = null;
  },
  UPDATE_TEACHER(state, teacher) {
    state.teacher = teacher;
  },
  ADD_TEACHER(state, teacher) {
    state.teacher = teacher;
  },
  CHANGE_TEACHER_PASSWORD() {
    // We don't store Password inside Vuex, so we don't need to do anything here
  },
  FREEZE_TEACHER() {
    // We only freeze Teacher, so we don't need to do anything here
  },
  SET_TAMAI_CLASSES(state, tamaiClasses) {
    state.tamaiClasses = tamaiClasses;
  },
  SET_ALL_TAMAI_CLASSES(state, tamaiClasses) {
    state.allTamaiClasses = tamaiClasses;
  },
};

export const actions = {
  setCurrentTeacherUser({ commit }, teacher) {
    commit('SET_TEACHER', teacher);
  },
  fetchTeachers({ commit }, { data }) {
    return TeacherService.all(data).then((response) => {
      commit('SET_TEACHERS', response.data);
    });
  },
  clearTeachers({ commit }) {
    commit('CLEAR_TEACHERS');
  },
  fetchTeacher({ commit }, id) {
    return TeacherService.get(id).then((response) => {
      commit('SET_TEACHER', response.data);
      return response.data;
    });
  },
  startEditingTeacher({ commit }, teacher) {
    commit('SET_EDITING_TEACHER', teacher);
  },
  finishEditingTeacher({ commit, state }, id) {
    if (state.editingTeacher) {
      let formattedTeacher = cloneDeep(state.editingTeacher);
      if (formattedTeacher.corporation) {
        formattedTeacher.corporationId = formattedTeacher.corporation.id;
        formattedTeacher = omit(formattedTeacher, ['corporation']);
      }
      if (formattedTeacher.brand) {
        formattedTeacher.brandId = formattedTeacher.brand.id;
        formattedTeacher = omit(formattedTeacher, ['brand']);
      }
      if (formattedTeacher.gender) {
        formattedTeacher.genderId = formattedTeacher.gender.id;
        formattedTeacher = omit(formattedTeacher, ['gender']);
      }
      if (formattedTeacher.nationality) {
        formattedTeacher.nationalityId = formattedTeacher.nationality.id;
        formattedTeacher = omit(formattedTeacher, ['nationality']);
      }
      if (formattedTeacher.employmentStatus) {
        formattedTeacher.employmentStatusId = formattedTeacher.employmentStatus.id;
        formattedTeacher = omit(formattedTeacher, ['employmentStatus']);
      }
      if (id) {
        return TeacherService.update(id, formattedTeacher).then((response) => {
          commit('DELETE_EDITING_TEACHER');
          commit('UPDATE_TEACHER', response.data);
        });
      } else {
        return TeacherService.create(formattedTeacher).then((response) => {
          commit('DELETE_EDITING_TEACHER');
          commit('ADD_TEACHER', response.data);
        });
      }
    }
  },
  clearEditingTeacher({ commit, state }) {
    if (state.editingTeacher) {
      commit('DELETE_EDITING_TEACHER');
    }
  },
  changeTeacherPassword({ commit }, { id, password }) {
    return TeacherService.changePassword(id, password).then(() => {
      commit('CHANGE_TEACHER_PASSWORD');
    });
  },
  freezeTeacher({ commit }, payload) {
    let formattedFrozenData = {};
    if (payload.frozenMonth != 'now') {
      formattedFrozenData['frozenDatetime'] = payload.frozenMonth;
    }
    return TeacherService.frozen(payload.id, formattedFrozenData).then(() => {
      commit('FREEZE_TEACHER');
    });
  },
  fetchTamaiClasses({ commit, state }) {
    if (state.teacher) {
      return TeacherService.tamaiClasses(state.teacher.id).then((response) => {
        commit('SET_TAMAI_CLASSES', response.data);
      });
    }
  },
  fetchAllTamaiClasses({ commit, state }, { data }) {
    return TeacherService.tamaiClasses(state.teacher.id, data).then((response) => {
      commit('SET_ALL_TAMAI_CLASSES', response.data);
    });
  },
};

export const getters = {
  getTeacherById: (state) => (id) => {
    return state.teachers.find((item) => item.id == id);
  },
  totalTeachers: (state) => {
    return state.teachers.length;
  },
  getExcelFormattedTeachers: (state) => (fields) => {
    return map(state.teachers, (teacher) => {
      let data = {
        法人名: basicSubFieldPresent(teacher, 'corporation', 'name'),
        ブランド名: basicSubFieldPresent(teacher, 'brand', 'name'),
        先生ID: basicPresent(teacher.uid),
        先生名: basicPresent(teacher.name),
        '担当クラス数（メイン）': basicPresent(teacher.numOfMainClasses).toString(),
        '担当クラス数（サブ）': basicPresent(teacher.numOfSubClasses).toString(),
        担当生徒人数: basicPresent(teacher.numOfStudents).toString(),
      };

      return pick(data, fields);
    });
  },
  existsRegistrationRequiredParams: (state) => {
    if (!state.editingTeacher.uid) return false;
    if (!state.editingTeacher.password) return false;
    if (!state.editingTeacher.name) return false;
    if (!state.editingTeacher.nameReading) return false;

    return true;
  },
  existsUpdateRequiredParams: (state) => {
    if (!state.editingTeacher.name) return false;
    if (!state.editingTeacher.nameReading) return false;

    return true;
  },
};
